import { TextLink } from '@rsa-digital/evo-shared-components/commonTypes/links';
import BreadcrumbsComponent from '@rsa-digital/evo-shared-components/components/Breadcrumbs';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { trackButtonClick } from 'helpers/eventTracking';
import { handleAndTrackLinkClick } from 'helpers/navigation';
import { generateJsonLdBreadcrumbs } from './jsonLd';
import { BreadcrumbsData } from './types';

type BreadcrumbsProps = {
  breadcrumbs: BreadcrumbsData;
  locale: string;
};

type CsSiteWideSeo = {
  breadcrumbs_home_link: {
    text: string;
    url: string;
  };
  breadcrumbs_back_to_top_text?: string;
  breadcrumbs_back_to_top_screenreader_text?: string;
  skip_back_to_top_from_tab_navigation: boolean;
  back_to_top_target_id?: string;
  locale: string;
};

type BreadcrumbsQueryData = {
  allCsSiteWideSeo: { nodes: CsSiteWideSeo[] };
};

const BreadcrumbsQuery = graphql`
  query {
    allCsSiteWideSeo {
      nodes {
        breadcrumbs_home_link {
          text
          url
        }
        breadcrumbs_back_to_top_text
        breadcrumbs_back_to_top_screenreader_text
        skip_back_to_top_from_tab_navigation
        back_to_top_target_id
        locale
      }
    }
  }
`;

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ breadcrumbs, locale }) => {
  const jsonLdBreadcrumbs = generateJsonLdBreadcrumbs(breadcrumbs);

  const { allCsSiteWideSeo } = useStaticQuery<BreadcrumbsQueryData>(BreadcrumbsQuery);

  const csSiteWideSeo = allCsSiteWideSeo.nodes.find(
    (siteWideSeo) => siteWideSeo.locale === locale
  );

  if (!csSiteWideSeo) {
    throw new Error(`Side Wide SEO for locale: ${locale} is missing!`);
  }

  const rootLink: TextLink = {
    text: csSiteWideSeo.breadcrumbs_home_link.text,
    url: csSiteWideSeo.breadcrumbs_home_link.url,
    onClick: handleAndTrackLinkClick(
      csSiteWideSeo.breadcrumbs_home_link.url,
      'breadcrumb',
      csSiteWideSeo.breadcrumbs_home_link.text
    ),
  };

  const breadcrumbsLinks = breadcrumbs.parent_links.map((breadcrumb) => ({
    text: breadcrumb.page_name,
    url: breadcrumb.url,
    onClick: handleAndTrackLinkClick(breadcrumb.url, 'breadcrumb', breadcrumb.page_name),
  }));

  return (
    <>
      <Helmet script={[jsonLdBreadcrumbs]} />
      <BreadcrumbsComponent
        breadcrumbs={breadcrumbsLinks}
        currentPageText={breadcrumbs.current_page_name}
        rootLink={rootLink}
        skipBackToTopOnTab={csSiteWideSeo.skip_back_to_top_from_tab_navigation}
        backToTopTargetId={
          !csSiteWideSeo.back_to_top_target_id
            ? 'header'
            : csSiteWideSeo.back_to_top_target_id
        }
        backToTopText={csSiteWideSeo.breadcrumbs_back_to_top_text}
        backToTopScreenreaderText={
          csSiteWideSeo.breadcrumbs_back_to_top_screenreader_text
        }
        onBackToTop={trackButtonClick('backToTop', 'Back To Top')}
        data-cy="Breadcrumbs"
      />
    </>
  );
};

export default React.memo(Breadcrumbs);
